import type { Matrix } from "@dopplexr/matrix/lib/matrix.ts";
import ky from "ky";
import { okAsync, ResultAsync } from "neverthrow";

const matrixAPI = import.meta.env.VITE_APP_GITEA_SIDECAR_API;

const baseURLCache: {
	[owner: string]: {
		[workspace: string]: {
			[project: string]: {
				[version: string]: {
					mediaPath: string;
					searchParams: Record<string, string>;
				};
			};
		};
	};
} = {};

export const getProductBaseURL = <
	T extends {
		owner: string;
		workspace: string;
		projectName: string;
		productVersion: string;
	},
>({
	owner,
	workspace,
	projectName,
	productVersion,
}: T) => {
	if (baseURLCache[owner]?.[workspace]?.[projectName]?.[productVersion]) {
		return okAsync(baseURLCache[owner][workspace][projectName][productVersion]);
	}

	baseURLCache[owner] ??= {};
	baseURLCache[owner][workspace] ??= {};
	baseURLCache[owner][workspace][projectName] ??= {};

	const res = ResultAsync.fromPromise(
		ky(
			`${matrixAPI}/owners/${owner}/workspaces/${workspace}/projects/${projectName}/versions/${productVersion}/media-path`,
			{ cache: "force-cache" },
		).json<{ mediaPath: string; searchParameters: Record<string, string> }>(),
		(err) => new Error("Error while fetching product info", { cause: err }),
	);

	return res.map((res) => {
		const output = {
			mediaPath: res.mediaPath,
			searchParams: res.searchParameters,
		};

		baseURLCache[owner][workspace][projectName][productVersion] = output;

		return output;
	});
};

export const loadFile = <T extends ArrayBuffer>(options: {
	owner: string;
	workspace: string;
	projectName: string;
	productVersion: string;
	filePath: string;
	responseType?: "buffer" | "json";
}) => {
	return getProductBaseURL(options).andThen(({ mediaPath, searchParams }) =>
		ResultAsync.fromPromise<Matrix | ArrayBuffer, Error>(
			ky(`${mediaPath}/${options.filePath}`, { searchParams }).then(
				(response) => {
					if (options.responseType === "buffer") {
						return response.arrayBuffer();
					}
					return response.json<T>();
				},
			),
			(err) => new Error("Error while fetching file", { cause: err }),
		),
	);
};
